<script setup lang="ts">
import ActivityFeed from '@/components/activity/ActivityFeed.vue';
import ProjectNoticeDialog from '@/components/projects/ProjectNoticeDialog.vue';
import PublishProjectDialog from '@/components/projects/PublishProjectDialog.vue';
import ProjectSummaryCard from '@/components/projects/ProjectSummaryCard.vue';
import TaskDetailsSheet from '@/components/tasks/TaskDetailsSheet.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import { useProjectQuickCreate } from '@/composables/projects/useProjectQuickCreate';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Activity, CheckCircle2, Clock3, FolderOpen, FolderPlus, ListTodo, Loader2 } from 'lucide-vue-next';
import { resolveOwnedClientId } from '~/lib/clientOwnership';
import { projectSettingsPath } from '~/lib/projectEditing';
import { isProjectOperational } from '~/lib/projectLifecycle';
import { toMillis } from '~/lib/timestamps';

definePageMeta({
    layout: 'app',
    middleware: ['tenant'],
});

const route = useRoute();
const router = useRouter();
const tenantStore = useTenantStore();
const { tenantPath } = useTenantPath();
const projectStore = useProjectStore();
const clientStore = useClientStore();
const taskStore = useTaskStore();
const approvalsStore = useApprovalsStore();
const commentsStore = useCommentsStore();
const filesStore = useFilesStore();
const { canMutate } = usePermissions();
const subscription = useWorkspaceSubscription();
const { canAutoStart: canAutoStartWorkspaceTour, startWelcomeTour } = useWorkspaceTour();
const { openProjectQuickCreate } = useProjectQuickCreate();
const {
    statusOptions,
    statusOptionDetails,
    assigneeOptions,
    priorityOptions,
    projectOptions,
    clientOptions,
} = useTaskTable();

const tenantSlug = computed(() => {
    const routeTenantSlug = route.params.tenantSlug;
    if (typeof routeTenantSlug === 'string' && routeTenantSlug) return routeTenantSlug;
    return String(tenantStore.currentTenantId || '');
});

const loading = ref(true);
const isPublishDialogOpen = ref(false);
const isNoticeDialogOpen = ref(false);
const isTrialWelcomeOpen = ref(false);
const offerWorkspaceTourAfterWelcome = ref(false);
const publishTargetProject = ref<any>(null);
const noticeTargetProject = ref<any>(null);
const showTaskDetail = ref(false);
const selectedTaskId = ref<string | null>(null);

const doneStatuses = new Set(['done', 'completed']);

function resolveClientAvatar(project: any): string {
    const resolvedClientId = resolveOwnedClientId(project);
    if (resolvedClientId) {
        const byId = clientStore.clients.find((client) => client.id === resolvedClientId);
        if (byId?.avatarURL) return byId.avatarURL;
    }

    const clientName = String(project.clientName || project.client || '').trim().toLowerCase();
    if (!clientName) return '';

    const byName = clientStore.clients.find((client) => {
        const labels = [client.companyName, client.name]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase());
        return labels.includes(clientName);
    });

    return byName?.avatarURL || '';
}

const clientAvatarByProjectId = computed(() => Object.fromEntries(
    projectStore.projects.map((project) => [project.id, resolveClientAvatar(project)]),
));

onMounted(async () => {
    loading.value = true;

    try {
        await Promise.all([
            projectStore.fetchProjects(),
            clientStore.fetchClients(),
            taskStore.fetchTasks(),
        ]);

        const projectIds = projectStore.projects.map(project => project.id);
        await Promise.all([
            approvalsStore.fetchApprovalsForProjects(projectIds),
            commentsStore.fetchCommentsForEntities('project', projectIds),
            filesStore.fetchFilesForEntities('project', projectIds),
        ]);
    } catch (error) {
        console.error('Failed to load projects overview data:', error);
    } finally {
        loading.value = false;
    }
});

const trialWelcomeStorageKey = computed(() => (
    tenantStore.currentTenantId ? `vuey:trial-welcome:${tenantStore.currentTenantId}` : ''
));

watch(
    () => [tenantStore.currentTenantId, subscription.entitlements.value.status, route.query.trial],
    () => {
        void maybeOpenTrialWelcome();
    },
    { immediate: true },
);

watch(isTrialWelcomeOpen, (open, previousOpen) => {
    if (!import.meta.client || open || !previousOpen || !trialWelcomeStorageKey.value) return;
    window.localStorage.setItem(trialWelcomeStorageKey.value, 'seen');
});

const projectStatsById = computed(() => {
    const stats: Record<string, {
        taskTotal: number,
        taskCompleted: number,
        commentsCount: number,
        filesCount: number,
        pendingApprovals: number,
    }> = {};

    for (const project of projectStore.projects) {
        const projectTasks = taskStore.tasks.filter(task => task.projectId === project.id);
        const taskCompleted = projectTasks.filter(task => doneStatuses.has(String(task.status || '').toLowerCase())).length;
        const commentsCount = commentsStore.commentsByEntity[`project:${project.id}`]?.length || 0;
        const filesCount = filesStore.filesByEntity[`project:${project.id}`]?.length || 0;
        const pendingApprovals = (approvalsStore.approvalsByProject[project.id] || [])
            .filter(approval => approval.status === 'pending')
            .length;

        stats[project.id] = {
            taskTotal: projectTasks.length,
            taskCompleted,
            commentsCount,
            filesCount,
            pendingApprovals,
        };
    }

    return stats;
});

const activeProjects = computed(() => (
    projectStore.projects.filter(project => isProjectOperational(project.status))
));

const metrics = computed(() => {
    const activeProjectIds = new Set(activeProjects.value.map(project => project.id));
    const tasks = (taskStore.tasks || []).filter(task => !task.projectId || activeProjectIds.has(task.projectId));
    const inProgress = tasks.filter(task => String(task.status || '') === 'In Progress').length;
    const highPriority = tasks.filter(task => ['high', 'critical'].includes(String(task.priority || '').toLowerCase())).length;
    const overdue = tasks.filter((task) => {
        if (!task.dueDate) return false;
        if (doneStatuses.has(String(task.status || '').toLowerCase())) return false;
        return new Date(task.dueDate).getTime() < Date.now();
    }).length;

    const dueSoon = tasks.filter((task) => {
        if (!task.dueDate) return false;
        if (doneStatuses.has(String(task.status || '').toLowerCase())) return false;
        const dueMs = new Date(task.dueDate).getTime();
        return dueMs >= Date.now() && dueMs <= Date.now() + (7 * 24 * 60 * 60 * 1000);
    }).length;

    const approvalsWaiting = activeProjects.value.reduce((total, project) => {
        const approvals = approvalsStore.approvalsByProject[project.id] || [];
        return total + approvals.filter(approval => approval.status === 'pending').length;
    }, 0);

    return {
        totalTasks: tasks.length,
        inProgress,
        highPriority,
        overdue,
        dueSoon,
        approvalsWaiting,
        activeProjects: activeProjects.value.length,
    };
});

const canCreateProjects = computed(() => canMutate.value && !subscription.projectLimitReached.value);
const projectLimitMessage = computed(() => {
    if (!subscription.projectLimitReached.value || subscription.hasUnlimitedProjects.value) {
        return '';
    }

    return `Your ${subscription.subscription.value.name} plan supports ${subscription.projectLimit.value} active projects. Upgrade to keep launching new work without archiving old ones first.`;
});

useScopedPageActions(computed(() => canCreateProjects.value
        ? [{
            id: 'new-project',
            label: 'New project',
            icon: FolderPlus,
            action: () => openProjectQuickCreate(),
        }]
        : []));
const trialEndsLabel = computed(() => {
    const time = toMillis(subscription.entitlements.value.trialEndsAt);
    if (!time) return 'soon';

    return new Date(time).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
});
const trialDaysRemaining = computed(() => {
    const time = toMillis(subscription.entitlements.value.trialEndsAt);
    if (!time) return 14;

    return Math.max(1, Math.ceil((time - Date.now()) / (24 * 60 * 60 * 1000)));
});

const statusBreakdown = computed(() => {
    const counts = new Map<string, number>();
    for (const task of taskStore.tasks) {
        const status = String(task.status || 'Unassigned');
        counts.set(status, (counts.get(status) || 0) + 1);
    }

    return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
});

function taskStatusHref(label: string) {
    const normalized = String(label || '').trim();
    if (!normalized) return tenantPath('/tasks');
    if (normalized === 'Unassigned') {
        return tenantPath({ path: '/tasks', query: { preset: 'no-status' } });
    }
    return tenantPath({ path: '/tasks', query: { status: normalized } });
}

const recentTasks = computed(() => {
    return [...taskStore.tasks]
        .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt))
        .slice(0, 6);
});

const recentProjects = computed(() => {
    return [...projectStore.projects]
        .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt))
        .slice(0, 6);
});

function openEditDialog(project: any) {
    void navigateTo(tenantPath(projectSettingsPath(project.slug || project.id)));
}

function openPublishDialog(project: any) {
    publishTargetProject.value = project;
    isPublishDialogOpen.value = true;
}

function openNoticeDialog(project: any) {
    noticeTargetProject.value = project;
    isNoticeDialogOpen.value = true;
}

function openTaskDetail(taskId: string) {
    selectedTaskId.value = taskId;
    showTaskDetail.value = true;
}

async function handleTaskUpdated() {
    await taskStore.fetchTasks();
}

async function handleTaskDeleted() {
    showTaskDetail.value = false;
    selectedTaskId.value = null;
    await taskStore.fetchTasks();
}

async function handleArchive(project: any) {
    await projectStore.updateProject(project.id, { status: 'Archived' });
}

async function handleDelete(project: any) {
    await projectStore.deleteProject(project.id);
}

async function maybeOpenTrialWelcome() {
    if (!import.meta.client) return;
    if (!subscription.entitlements.value.isFullFeatureTrial) return;
    if (!trialWelcomeStorageKey.value) return;

    const forcedByOnboarding = route.query.trial === 'welcome';
    const alreadySeen = window.localStorage.getItem(trialWelcomeStorageKey.value) === 'seen';

    if (!forcedByOnboarding && alreadySeen) return;

    isTrialWelcomeOpen.value = true;
    offerWorkspaceTourAfterWelcome.value = forcedByOnboarding && canAutoStartWorkspaceTour.value;

    if (forcedByOnboarding) {
        await router.replace({
            path: route.path,
            query: {
                ...route.query,
                trial: undefined,
            },
        });
    }
}

async function dismissTrialWelcome(options: { startTour?: boolean } = {}) {
    if (import.meta.client && trialWelcomeStorageKey.value) {
        window.localStorage.setItem(trialWelcomeStorageKey.value, 'seen');
    }

    isTrialWelcomeOpen.value = false;
    const shouldStartTour = options.startTour ?? offerWorkspaceTourAfterWelcome.value;
    offerWorkspaceTourAfterWelcome.value = false;

    if (shouldStartTour) {
        await nextTick();
        void startWelcomeTour();
    }
}

async function openSubscriptionSettings() {
    await dismissTrialWelcome({ startTour: false });
    await navigateTo(tenantPath('/settings/subscription'));
}

function formatRelativeTime(value: unknown): string {
    const time = toMillis(value);
    if (!time) return 'Unknown';

    const diff = Date.now() - time;

    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return `${Math.floor(diff / 604_800_000)}w ago`;
}

function taskPriorityTone(priority?: string) {
    if (priority === 'critical') return 'destructive';
    if (priority === 'high') return 'secondary';
    if (priority === 'medium') return 'default';
    return 'outline';
}
</script>

<template>
    <div class="page-shell overflow-x-hidden">
        <Dialog v-model:open="isTrialWelcomeOpen">
            <DialogContent class="editor-dialog-shell max-w-3xl overflow-hidden sm:max-w-none" :style="{ width: 'min(92vw, 960px)' }">
                <DialogHeader class="space-y-4 border-b border-border/60 pb-5">
                    <div class="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">14-day free trial</Badge>
                        <Badge variant="outline">Everything unlocked</Badge>
                        <Badge variant="outline">Ends {{ trialEndsLabel }}</Badge>
                    </div>
                    <DialogTitle class="text-3xl tracking-tight">Welcome to your Vuey trial</DialogTitle>
                    <DialogDescription class="max-w-2xl text-sm leading-6 text-muted-foreground">
                        Your workspace has full Pro access for the next {{ trialDaysRemaining }} day{{ trialDaysRemaining === 1 ? '' : 's' }}.
                        Explore workflows, recurring tasks, analytics, custom domains, branded email, and the full white-label experience before you choose your long-term plan.
                    </DialogDescription>
                </DialogHeader>

                <div class="grid gap-4 py-6 md:grid-cols-3">
                    <Card>
                        <CardContent class="space-y-3 p-5">
                            <p class="text-sm font-semibold">Starter</p>
                            <p class="text-sm text-muted-foreground">For freelancers and smaller teams getting organised.</p>
                            <p class="text-2xl font-semibold">£19<span class="text-sm font-normal text-muted-foreground">/mo</span></p>
                            <p class="text-sm text-muted-foreground">Core CRM, client portal, tasks, projects, quotes, invoices, and Stripe.</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent class="space-y-3 p-5">
                            <div class="flex items-center justify-between gap-3">
                                <p class="text-sm font-semibold">Growth</p>
                                <Badge variant="secondary">Most popular</Badge>
                            </div>
                            <p class="text-sm text-muted-foreground">For growing agencies that need repeatable delivery systems.</p>
                            <p class="text-2xl font-semibold">£59<span class="text-sm font-normal text-muted-foreground">/mo</span></p>
                            <p class="text-sm text-muted-foreground">Adds workflows, templates, recurring tasks, approvals, analytics, deposits, and payment plans.</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent class="space-y-3 p-5">
                            <p class="text-sm font-semibold">Pro</p>
                            <p class="text-sm text-muted-foreground">For studios that want the full white-label client experience.</p>
                            <p class="text-2xl font-semibold">£99<span class="text-sm font-normal text-muted-foreground">/mo</span></p>
                            <p class="text-sm text-muted-foreground">Adds custom domain, branded sender domain, full platform branding, and priority support.</p>
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardContent class="px-5 py-4 text-sm text-muted-foreground">
                        Your workspace is saved on Starter billing by default, but the full Pro feature set is unlocked throughout the trial so you can test everything properly before upgrading.
                    </CardContent>
                </Card>

                <DialogFooter class="border-t border-border/60 pt-5">
                    <Button variant="outline" @click="dismissTrialWelcome()">
                        Start exploring
                    </Button>
                    <Button @click="openSubscriptionSettings">
                        Review plans
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <FeatureGateNotice
            v-if="projectLimitMessage"
            compact
            title="Project limit reached"
            :description="projectLimitMessage"
            requirement="Upgrade for more active projects"
        />

        <Card v-if="loading" class="min-h-32 items-center justify-center gap-2 px-4 text-sm text-muted-foreground">
            <Loader2 class="size-4 animate-spin" />
            Loading projects overview...
        </Card>

        <template v-else>
            <div class="overview-metrics-grid">
                <MetricCard density="compact" label="Active projects" :value="metrics.activeProjects" :icon="FolderOpen" :href="tenantPath('/projects')" />
                <MetricCard density="compact" label="Open tasks" :value="metrics.totalTasks" :icon="ListTodo" :href="tenantPath({ path: '/tasks', query: { preset: 'open' } })" />
                <MetricCard density="compact" label="In progress" :value="metrics.inProgress" :icon="Clock3" :href="tenantPath({ path: '/tasks', query: { status: 'In Progress' } })" />
                <MetricCard density="compact" label="Awaiting approval" :value="metrics.approvalsWaiting" :icon="CheckCircle2" :href="tenantPath({ path: '/projects', query: { attention: 'approvals' } })" />
            </div>

            <div class="overview-columns">
                <SectionSurface
                    class="min-w-0"
                    title="Projects At A Glance"
                    description="The same project rows used throughout the rest of the product."
                >
                    <template #actions>
                        <Badge variant="outline" class="hidden sm:inline-flex">{{ recentProjects.length }} shown</Badge>
                    </template>

                    <EmptyState
                        v-if="recentProjects.length === 0"
                        title="No projects yet"
                        description="Add your first project to start tracking delivery work."
                        class="min-h-32"
                    />

                    <div v-else class="divide-y divide-border/20">
                        <div v-for="project in recentProjects" :key="project.id">
                            <div class="md:hidden">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    compact
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @delete="handleDelete"
                                />
                            </div>
                            <div class="hidden md:block">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @delete="handleDelete"
                                />
                            </div>
                        </div>
                    </div>
                </SectionSurface>

                <section class="min-w-0 space-y-4">
                    <SectionSurface
                        title="Task Status"
                        description="A compact breakdown of the work currently moving through the system."
                    >
                        <template #actions>
                            <Badge variant="outline">{{ metrics.dueSoon }} due soon</Badge>
                        </template>

                        <EmptyState
                            v-if="statusBreakdown.length === 0"
                            title="No task activity yet"
                            description=""
                            class="min-h-24 py-6"
                        />

                        <div v-else class="subtle-row-list">
                            <NuxtLink
                                v-for="item in statusBreakdown"
                                :key="item.label"
                                :to="taskStatusHref(item.label)"
                                class="subtle-row-link block"
                            >
                                <div class="space-y-2.5">
                                    <div class="flex items-center justify-between gap-3 text-sm">
                                        <span class="font-medium text-foreground">{{ item.label }}</span>
                                        <span class="text-muted-foreground">{{ item.count }}</span>
                                    </div>
                                    <div class="h-2 overflow-hidden rounded-full bg-muted">
                                        <div
                                            class="h-full rounded-full bg-foreground/80 transition-all duration-300"
                                            :style="{ width: `${metrics.totalTasks ? Math.max((item.count / metrics.totalTasks) * 100, 6) : 0}%` }"
                                        />
                                    </div>
                                </div>
                            </NuxtLink>
                        </div>
                    </SectionSurface>

                    <SectionSurface
                        title="Recent Tasks"
                        description="The latest delivery activity across projects."
                    >
                        <template #actions>
                            <Badge v-if="metrics.highPriority > 0" variant="secondary">{{ metrics.highPriority }} high priority</Badge>
                        </template>

                        <EmptyState
                            v-if="recentTasks.length === 0"
                            title="No tasks yet"
                            description=""
                            class="min-h-24 py-6"
                        />

                        <div v-else class="subtle-row-list">
                            <button
                                v-for="task in recentTasks"
                                :key="task.id"
                                type="button"
                                class="subtle-row-link block w-full text-left"
                                @click="openTaskDetail(task.id)"
                            >
                                <div class="flex items-start justify-between gap-3">
                                    <div class="min-w-0 flex-1">
                                        <p class="truncate text-sm font-medium text-foreground">{{ task.title }}</p>
                                        <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                            <span>{{ task.status || 'No status' }}</span>
                                            <span>•</span>
                                            <span>{{ formatRelativeTime(task.updatedAt || task.createdAt) }}</span>
                                        </div>
                                    </div>
                                    <Badge v-if="task.priority" :variant="taskPriorityTone(task.priority) as any">
                                        {{ task.priority }}
                                    </Badge>
                                </div>
                            </button>
                        </div>
                    </SectionSurface>
                </section>
            </div>

            <SectionSurface
                class="min-w-0"
                title="Recent Activity"
                description="A live feed of what the team has been doing most recently."
            >
                <ActivityFeed :max-items="15" />
            </SectionSurface>

            <PublishProjectDialog
                :open="isPublishDialogOpen"
                :project="publishTargetProject"
                @update:open="(value) => { isPublishDialogOpen = value; if (!value) publishTargetProject = null; }"
                @saved="publishTargetProject = null"
            />

            <ProjectNoticeDialog
                :open="isNoticeDialogOpen"
                :project="noticeTargetProject"
                @update:open="(value) => { isNoticeDialogOpen = value; if (!value) noticeTargetProject = null; }"
                @saved="noticeTargetProject = null"
            />

            <TaskDetailsSheet
                v-model:open="showTaskDetail"
                :task-id="selectedTaskId"
                :status-options="statusOptions"
                :status-option-details="statusOptionDetails"
                :assignee-options="assigneeOptions"
                :priority-options="priorityOptions"
                :project-options="projectOptions"
                :client-options="clientOptions"
                @task-updated="handleTaskUpdated"
                @task-deleted="handleTaskDeleted"
            />
        </template>
    </div>
</template>
